import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Button } from '../../components/Button';
import { Loader } from '../../components/Loader';
import { Badge } from '../../components/Badge';
import { FeaturesList } from '../../components/FeaturesList';
import { SmallNotice } from '../../components/SmallNotice';
import { InfoModal } from '../../components/InfoModal';
import CustomLink from '../../components/CustomLink';
import { NoData } from '../../components/NoData';
import { useDocumentTitle } from '../../shared/use-document-title';
import { useSubscriptions } from '../../shared/use-subscriptions';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { ACTIVE, CANCELED } from '../../../lib/shared/subscription-statuses';
import { uiConfig } from '../../app-ui-config';
import { SUPPORT_EMAIL, SUPPORT_PHONE, SUPPORT_PHONE_DISPLAY } from '../../shared/links';
import premiumImageSrc from '../../assets/images/premium.png';
import styles from './AccountPage.css';

const PRE_CANCEL_SUBSCRIPTION_MODAL = 'preCancelSubscriptionModal';
const SUBSCRIPTION_CANCELED_MODAL = 'subscriptionCanceledModal';

const cx = classnames.bind(styles);

function AccountPage() {
    const { subscriptions, isLoading, cancelSubscription, isPending, preCancelData, setPreCancelData } = useSubscriptions();
    const [modal, setModal] = useState({ type: '' });

    useEffect(() => {
        if (window.location.hash && !isLoading) {
            document.querySelector(window.location.hash)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    }, [isLoading]);

    useDocumentTitle('My account');

    const openModalForCancelSubscription = subscriptionData => () => {
        logEvent(EVENT_TYPES.CANCEL_SUBSCRIPTION_BUTTON_CLICKED);
        setModal({ type: PRE_CANCEL_SUBSCRIPTION_MODAL });
        setPreCancelData(subscriptionData);
    };

    const closeModal = () => {
        setModal({ type: '' });
        setPreCancelData({});
    };

    const renderNextPaymentText = ({ status, isActive, validUntilDate, nextChargeDate }) => {
        if (status === ACTIVE) {
            return <span>{nextChargeDate}</span>;
        }

        if (status === CANCELED && isActive) {
            return (
                <span className={styles.warning}>
                    You will not be charged again. Your plan ends on <strong>{validUntilDate}</strong>.
                </span>
            );
        }

        return <span className={styles.warning}><strong>You will not be charged again</strong></span>;
    };

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className={styles.loaderWrapper}>
                    <Loader variant='secondary' />
                </div>
            );
        }

        if (subscriptions.length === 0) {
            return <NoData title='No Subscriptions' />;
        }

        return (
            <ul className={styles.subscriptions}>
                {subscriptions.map(subscriptionData => {
                    const isSubscriptionActive = subscriptionData.status === ACTIVE;

                    return (
                        <li
                            key={subscriptionData.id}
                            className={styles.subscriptionCard}
                            id={`subscription-${subscriptionData.id}`}
                        >
                            <div className={styles.subscriptionCardHeader}>
                                <img
                                    src={`${premiumImageSrc}?v=2`}
                                    alt='premium'
                                />
                                <Badge
                                    variant={isSubscriptionActive ? 'success' : 'error'}
                                    label={isSubscriptionActive ? 'Active subscription' : 'canceled'}
                                    iconName={isSubscriptionActive ? 'shopping' : 'alertTriangle'}
                                />
                            </div>
                            <div className={cx('subscriptionCardTitleBlock', !isSubscriptionActive ? 'blockWithBorder' : '')}>
                                <h2 className={styles.subscriptionCardTitle}>{subscriptionData.name}</h2>
                                {subscriptionData.hasTrial && Boolean(subscriptionData.trial?.billingPeriod) && (
                                    <p className={styles.subscriptionCardTrialText}>
                                        {subscriptionData.trial.billingPeriod.value}-{subscriptionData.trial.billingPeriod.value === 1 ? subscriptionData.trial.billingPeriod.unit : `${subscriptionData.trial.billingPeriod.unit}s`} trial price {subscriptionData.trial.price}
                                    </p>
                                )}
                            </div>
                            {isSubscriptionActive && (
                                <Button
                                    onClick={openModalForCancelSubscription(subscriptionData)}
                                    label='Cancel subscription'
                                    type='secondary'
                                    className={styles.buttonCancel}
                                />
                            )}
                            <ul className={cx('subscriptionCardInfoItems', isSubscriptionActive ? 'blockWithBorder' : '')}>
                                <li className={styles.subscriptionCardInfoItem}>
                                    <span>Price</span>
                                    <span>{subscriptionData.price}</span>
                                </li>
                                <li className={styles.subscriptionCardInfoItem}>
                                    <span>Plan renews</span>
                                    <span>{subscriptionData.billingPeriodLabel}</span>
                                </li>
                                <li className={styles.subscriptionCardInfoItem}>
                                    <span>Plan started</span>
                                    <span>{subscriptionData.createdAtDate}</span>
                                </li>
                                <li className={styles.subscriptionCardInfoItem}>
                                    <span>Next payment</span>
                                    {renderNextPaymentText(subscriptionData)}
                                </li>
                            </ul>
                            {isSubscriptionActive && (
                                <>
                                    <p className={styles.subscriptionCardCancelText}>
                                        <strong>If you cancel your subscription, you will no longer have access to:</strong>
                                    </p>
                                    <FeaturesList type={subscriptionData.plan?.plan} />
                                    <p className={styles.subscriptionCardSupportText}>
                                        If you have any questions regarding this purchase, please contact our support team at{' '}
                                        <CustomLink
                                            to={`mailto:${SUPPORT_EMAIL}`}
                                            target='custom'
                                            className={styles.link}
                                            onClick={() => logEvent(EVENT_TYPES.SUPPORT_EMAIL_CLICKED)}
                                        >
                                            {SUPPORT_EMAIL}
                                        </CustomLink>
                                        {' '}or call us at{' '}
                                        <CustomLink
                                            to={`tel:${SUPPORT_PHONE}`}
                                            target='custom'
                                            className={styles.link}
                                            onClick={() => logEvent(EVENT_TYPES.SUPPORT_PHONE_CLICKED)}
                                        >
                                            {SUPPORT_PHONE_DISPLAY}
                                        </CustomLink>
                                    </p>
                                </>
                            )}
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <>
            {renderContent()}
            <InfoModal
                isOpen={modal.type === PRE_CANCEL_SUBSCRIPTION_MODAL}
                onCloseModal={closeModal}
                className={cx('modal', 'preCancelSubscriptionModal')}
                title='Cancel Subscription?'
                renderButtons={() => (
                    <>
                        <Button
                            onClick={cancelSubscription(() => setModal({ type: SUBSCRIPTION_CANCELED_MODAL }))}
                            label='Confirm'
                            size='medium'
                            className={styles.buttonConfirm}
                            isLoading={isPending}
                        />
                        <Button
                            onClick={closeModal}
                            label='Decline'
                            type='secondary'
                            size='medium'
                        />
                    </>
                )}
            >
                <SmallNotice
                    iconName='info'
                    description='Are you sure you want to cancel?'
                    withBackground
                />
            </InfoModal>
            <InfoModal
                isOpen={modal.type === SUBSCRIPTION_CANCELED_MODAL}
                onCloseModal={closeModal}
                title='Subscription Canceled'
                className={cx('modal', 'canceledSubscriptionModal')}
            >
                <SmallNotice
                    iconName='checkNotice'
                    description={`Your plan is active until ${preCancelData.validUntilDate}. You may continue to use ${uiConfig.name} as usual until then.`}
                    withBackground
                    className={styles.smallNotice}
                />
            </InfoModal>
        </>
    );
}

export {
    AccountPage
};
